import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Camera, RefreshCw, X, Check, RotateCcw } from 'lucide-react';

// In-app camera: live preview → capture → "Use photo" / "Retake".
//
// A plain <input type="file" capture> only opens the camera on phones and
// tablets; desktop browsers ignore `capture` and show the file chooser. The
// camera API works everywhere the page is served securely (https or
// localhost), with the PC webcam or the tablet's back camera.
export const cameraAvailable = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  && (typeof window === 'undefined' || window.isSecureContext !== false);

const CameraCaptureModal = ({ open, onClose, onCapture, onFallback }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState('environment');
  const [shot, setShot] = useState(null);       // { blob, url } once captured
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      setError(err?.name === 'NotAllowedError' ? t('framePhoto.cameraDenied') : t('framePhoto.cameraUnavailable'));
    }
  }, [stop, t]);

  useEffect(() => {
    if (open && !shot) start(facing);
    return () => stop();
  }, [open, facing, shot, start, stop]);

  useEffect(() => {
    if (!open) {
      setShot((prev) => { if (prev) URL.revokeObjectURL(prev.url); return null; });
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      stop();
      setShot({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.92);
  };

  const usePhoto = () => {
    const file = new File([shot.blob], `frame-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
    onClose();
  };

  const retake = () => {
    URL.revokeObjectURL(shot.url);
    setShot(null);
  };

  const btn = (primary) => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', borderRadius: 10,
    border: primary ? 'none' : '1px solid rgba(255,255,255,0.35)', background: primary ? '#4f46e5' : 'rgba(255,255,255,0.08)',
    color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
  });

  return ReactDOM.createPortal(
    <div role="dialog" aria-modal="true" aria-label={t('framePhoto.takePhoto')}
      style={{ position: 'fixed', inset: 0, zIndex: 6000, background: 'rgba(2,6,23,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem', gap: '0.9rem' }}>
      <div style={{ position: 'relative', width: 'min(92vw, 720px)', aspectRatio: '4 / 3', background: '#0f172a', borderRadius: 14, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {shot
          ? <img src={shot.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <video ref={videoRef} playsInline muted autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', transform: facing === 'user' ? 'scaleX(-1)' : 'none' }} />}
        {error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '1.5rem', textAlign: 'center', color: '#e2e8f0', fontSize: '0.9rem' }}>
            <span>{error}</span>
            {onFallback && (
              <button type="button" style={btn(false)} onClick={() => { onClose(); onFallback(); }}>
                {t('framePhoto.chooseFromGallery')}
              </button>
            )}
          </div>
        )}
        <button type="button" onClick={onClose} aria-label={t('common.close')}
          style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.7)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {shot ? (
          <>
            <button type="button" style={btn(false)} onClick={retake}><RotateCcw size={16} /> {t('framePhoto.retake')}</button>
            <button type="button" style={btn(true)} onClick={usePhoto}><Check size={16} /> {t('framePhoto.usePhoto')}</button>
          </>
        ) : (
          <>
            <button type="button" style={btn(false)} onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}>
              <RefreshCw size={16} /> {t('framePhoto.switchCamera')}
            </button>
            <button type="button" style={{ ...btn(true), opacity: ready ? 1 : 0.5 }} disabled={!ready} onClick={capture}>
              <Camera size={16} /> {t('framePhoto.capture')}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CameraCaptureModal;
